import { Box, Button, Center, Flex, Grid, GridItem, Image, Input, InputGroup, InputLeftElement, Popover, PopoverBody, PopoverCloseButton, PopoverContent, PopoverTrigger, Spinner, Text, } from '@chakra-ui/react';
import MyDiv from './employeeList.style';
import { EmployeeCard, EmployeeTable } from './components';
import { EmployeeModel } from '../../../models/employee';
import { Search2Icon } from '@chakra-ui/icons';
import { AlertCard, CustomButton, CustomPagination, CustomToast, FilterPopover } from '../../../components';
import CardIcon from '../../../assets/images/CardIcon.svg';
import ListIcon from '../../../assets/images/ListIcon.svg';
import FilterIcon from '../../../assets/images/FilterIcon.svg';
import { useEffect, useRef, useState } from 'react';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, } from '@chakra-ui/react'
import * as routesNames from "../../../constants/routes"
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ILazyParams, useFetch } from '../../../hooks/useFetch';
import { IDepartment } from '../../Admin/Department/components/DepartmentList/model';
import { IFilterOptions } from '../../../components/FilterCard/filterCard';
import { useAuth } from '../../../hooks/useAuth';
import { IEmployeeData, sortMapping, sortOptions, StatusDD } from './model';
import SortIcon from '../../../assets/images/SortIcon.svg';


const EmployeeList = () => {
  const [viewMode, setViewMode] = useState<'card' | 'list'>('card');
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const [initialLoading, setInitialLoading] = useState(true);
  const searchRef = useRef<HTMLInputElement>(null);
  const [statusDD, setStatusDD] = useState<StatusDD[]>([]);
  const [employees, setEmployees] = useState<{ data: EmployeeModel[], totalCount: number }>({ data: [], totalCount: 0, });
  const [reload, setReload] = useState(false);
  const [query, setQuery] = useState(() => {
    const savedFilter = sessionStorage.getItem("employeeFilter");
    return {
      lazyParams: { first: 0, rows: 9, page: 0, sortField: '', sortOrder: 1 },
      filter: savedFilter ? JSON.parse(savedFilter) : {}
    };
  });

  const [filter, setFilter] = useState<IFilterOptions>({ filterOptions: {} });
  const location = useLocation();
  const initialDepartment: IDepartment | null = location.state?.department ?? null;
  const [selectedDepartment, setSelectedDepartment] = useState<IDepartment | null>(initialDepartment);
  const showDepartment = location.state?.showDepartment ?? true;
  const [search, setSearch] = useState<string>('');
  const [debouncedSearch, setDebouncedSearch] = useState<string>('');
  const [selected, setSelected] = useState<string>(sortOptions[0]);
  const [sortpopoverOpen, setSortpopoverOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState<{
    description: string;
    colorScheme: string;
    onConfirm: () => void;
  } | null>(null);
  const pageCount = Math.ceil(employees.totalCount / query.lazyParams.rows);

  const handleAddNewEmployee = () => {
    if (hasPermission('Employee', 'Add New Employee')) {
      navigate(routesNames.ADDNEWEMPLOYEE);
    }
  };

  const handlePageClick = ({ selected }: { selected: number }) => {
    setQuery((prev) => {
      const updatedParams = {
        ...prev.lazyParams,
        page: selected,
        first: selected * prev.lazyParams.rows,
      };
      return { ...prev, lazyParams: updatedParams };
    });
  };

  // handle rows per page change
  const setRowsPerPage = (rows: number) => {
    setQuery((prev) => {
      const updatedParams = {
        ...prev.lazyParams,
        rows,
        page: 0, // reset to first page
        first: 0,
      };
      return { ...prev, lazyParams: updatedParams };
    });
  };

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 1000);
    return () => {
      clearTimeout(handler);
    };
  }, [search]);

  const handleExportClick = () => {
    setAlertConfig({
      description: "Are you sure you want to export employee data?",
      colorScheme: "green",
      onConfirm: () => exortdata(),
    });
  };


  async function exortdata() {
    try {
      const queryParams = new URLSearchParams({
        department_Id: `${selectedDepartment?.id ?? 0}`,
        filter: JSON.stringify(query.filter ?? {}),
        lazyParams: JSON.stringify(query.lazyParams),
        search: debouncedSearch ?? '',
      }).toString();
      const response = await fetchApi("Hrlense_Employee/ExportEmployeeExcel", "GET", null, queryParams, undefined, undefined, true);
      if (!response || !response.ok) {
        addToast({ message: "Failed to export data", status: "error" });
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url;
      a.download = "EmployeeList.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Export error:", error);
    }
  }



  async function loadEmployees() {
    setInitialLoading(true);
    const _query = new URLSearchParams({
      department_Id: `${selectedDepartment?.id ?? 0}`,
      lazyParams: JSON.stringify(query.lazyParams),
      filter: JSON.stringify(query.filter ?? {}),
      search: debouncedSearch ?? '',
    }).toString();

    const result = await fetchApi<IEmployeeData>('Hrlense_Employee', 'GET', null, _query);
    if (result) {
      if (result.employee.length === 0) {
        addToast({ message: "Employee not found.", status: "info" });
      }
      setEmployees({ data: result.employee, totalCount: result.totalCount });
    }
    setInitialLoading(false);
  }

  async function loadEmployeeFilter() {
    const response = await fetchApi<IFilterOptions>('Filters/EmployeeFilter', 'GET', null, `showDepartment=${showDepartment ? true : false}`, '');
    if (response) {
      setFilter(response);
    }
  }

  useEffect(() => {
    const getStatusDropdown = async () => {
      try {
        const response = await fetchApi("DropDown/EmplyeeStatus", "GET", null, "", "");
        if (response?.length > 0) {
          const modifiedStatusDD = response.filter((item: any) =>
            ["Active", "Terminated", "Release", "Abscond"].includes(item.employeeStatus)
          );
          setStatusDD(modifiedStatusDD);
        } else {
          addToast({ message: "Empty employee status dropdown.", status: "error" })
        }
      } catch (error) {
        console.error("Failed to load employee status", error);
      }
    };

    getStatusDropdown();
  }, []);




  useEffect(() => {
    loadEmployeeFilter();
  }, [showDepartment]);


  useEffect(() => {
    loadEmployees();
  }, [query, reload, debouncedSearch]);

  const handleSelect = (option: string) => {
    setSelected(option);
    const { field, order } = sortMapping[option];
    setQuery((prev) => {
      const updatedParams = {
        ...prev.lazyParams,
        sortField: field,
        sortOrder: order,
        page: 0,
        first: 0,
      };
      return {
        ...prev,
        lazyParams: updatedParams,
      };
    });

    setSortpopoverOpen(false);
  };

  return (
    <MyDiv>
      <Box className="page_wrapper">
        <Flex className="page_heading flex_header">
          <Flex className="page_heading flex_header">
            <Breadcrumb spacing="5px" className="font_poppins text_md text_medium" separator=">>">
              <BreadcrumbItem key="emp">
                <BreadcrumbLink className="font_poppins text_md text_medium" color="gray.500" onClick={() => {
                 sessionStorage.removeItem("employeeFilter");
                  navigate(routesNames.EMPLOYEES, {
                    replace: true,
                    state: null
                  });
                  setSelectedDepartment(null);
                  setQuery({
                    lazyParams: { first: 0, rows: 9, page: 0, sortField: '', sortOrder: 1 },
                    filter: {}
                  });
                }}
                >
                  Employee
                </BreadcrumbLink>
              </BreadcrumbItem>
              {selectedDepartment ? (
                <>
                  <Breadcrumb spacing="5px" className="font_poppins text_md text_medium" separator=">>">
                    <BreadcrumbItem key="dept">
                      <BreadcrumbLink className="font_poppins text_md text_medium" color="gray.500" as={Link} to={routesNames.ADMINDEPARTMENT}>
                        Department
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem key="selectedDept" isCurrentPage>
                      <BreadcrumbLink className="font_poppins text_md text_medium" color="gray.500">
                        {selectedDepartment.departmentName}
                      </BreadcrumbLink>
                    </BreadcrumbItem>
                  </Breadcrumb>
                </>
              ) : (
                <BreadcrumbItem key="all" isCurrentPage>
                  <BreadcrumbLink className="font_poppins text_md text_medium" href="#">
                    All Department
                  </BreadcrumbLink>
                </BreadcrumbItem>
              )}
            </Breadcrumb>

          </Flex>
          <Flex gap={2} align="center">
            <InputGroup width="230px" height="37px" >
              <InputLeftElement pointerEvents="none">
                <Search2Icon color="rgba(102, 112, 133, 1)" />
              </InputLeftElement>
              <Input onChange={(e) => handleSearch(e.target.value)} ref={searchRef} type="text" className="font-poppins text_regular text_md" placeholder="Search employee name" maxW="300px" />
            </InputGroup>
            <Popover placement="bottom-end" closeOnBlur>
              <PopoverTrigger>
                <CustomButton className="filterView font-poppins text_regular text_md" title="Filter" leftIcon={<Image src={FilterIcon} alt="Filter" />} />
              </PopoverTrigger>
              <FilterPopover
                filter={filter} initialFilters={query.filter}
                onApplyFilter={(filters) => {
                  const _filter: Record<string, number[]> = {};
                  Object.keys(filters).forEach((key) => {
                    if (filters[key].length > 0) { _filter[key.replace(' ', '')] = filters[key]; }
                  });
                  if (Object.keys(_filter).length === 0) {
                    sessionStorage.removeItem("employeeFilter");
                  } else {
                   sessionStorage.setItem("employeeFilter", JSON.stringify(_filter));
                  }
                  setQuery(prev => ({ ...prev, lazyParams: { ...prev.lazyParams, page: 0, first: 0 }, filter: _filter }));
                }}
              />
              {hasPermission('Employee', 'ExportData') && (
                <Button className="font-poppins text_sm text_md" bg="#006838" variant="solid" color="#fff" _hover={{ bg: '#005529' }} onClick={handleExportClick}  >Export</Button>)}
            </Popover>
            <Popover
              isOpen={sortpopoverOpen}
              onClose={() => setSortpopoverOpen(false)}
              placement="bottom-end"
            >
              <PopoverTrigger>
                <Image
                  src={SortIcon}
                  alt="Sort"
                  className='icon_btn'
                  onClick={() => setSortpopoverOpen(true)}
                  cursor="pointer"
                />
              </PopoverTrigger>
              <PopoverContent w="270px" borderRadius="10px" boxShadow="md" textAlign={'center'}>
                <Box px={4} py={2} borderBottom="1px solid #eee">
                  <Text className='font-poppins text_sm text_medium' textAlign="center" color={"#00000080"}>Sort By</Text>
                  <PopoverCloseButton top="8px" right="12px" />
                </Box>
                <PopoverBody p={0}>
                  {sortOptions.map((option) => (
                    <Box
                      key={option}
                      px={4}
                      py={2}
                      cursor="pointer"
                      bg={selected === option ? '#EBEBEB' : 'white'}
                      _hover={{ bg: '#EBEBEB' }}
                      onClick={() => handleSelect(option)}
                    >
                      <Text className='font-poppins font_dark' fontSize="14px" fontWeight={selected === option ? '500px' : '500px'}>{option}</Text>
                    </Box>
                  ))}
                </PopoverBody>
              </PopoverContent>
            </Popover>

            <Button bg={viewMode === 'list' ? 'rgba(234, 236, 240, 1)' : 'transparent'} _hover={{ bg: 'rgba(234, 236, 240, 0.8)' }} className="listView" width="44px" height="43px" padding="0" onClick={() => setViewMode('list')}>
              <Image src={ListIcon} alt="List" w="26px" h="26px" margin="auto" />
            </Button>
            <Button className="cardView" bg={viewMode === 'card' ? 'rgba(234, 236, 240, 1)' : 'transparent'} _hover={{ bg: 'rgba(234, 236, 240, 0.8)' }} width="44px" height="43px" padding="0" onClick={() => setViewMode('card')}>
              <Image src={CardIcon} alt="Card" w="22px" h="22px" margin="auto" />
            </Button>
            {hasPermission('Employee', 'Add New Employee') && (
              <Button className="font-poppins text_sm text_md" bg="#006838" variant="solid" color="#fff" _hover={{ bg: '#005529' }} onClick={handleAddNewEmployee}>Add New Employee</Button>)}
          </Flex>
        </Flex>
        <Box position="relative" minH="300px">
          {initialLoading ? (
            <Center position="absolute" top={0} left={0} right={0} bottom={0}>
              <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#006838" size="xl" />
            </Center>
          ) : (
            <>
              {viewMode === 'card' ? (
                <Grid className="grid_container" rowGap={4} columnGap={4}>
                  {employees.data?.map((emp) => (
                    <GridItem colSpan={4} key={emp.id}>
                      <EmployeeCard employee={emp} statusDD={statusDD} reload={reload} setReload={setReload} />
                    </GridItem>
                  ))}
                </Grid>
              ) : (
                <Grid className="grid_container">
                  <GridItem colSpan={12} >
                    <EmployeeTable employees={employees.data} statusDD={statusDD} reload={reload} setReload={setReload} />
                  </GridItem>
                </Grid>
              )}
            </>
          )}
        </Box>
        <CustomPagination pageCount={pageCount} rowsPerPageOptions={[9, 30, 90, 210]} handlePageClick={handlePageClick} rowsPerPage={query.lazyParams.rows} setRowsPerPage={setRowsPerPage} totalRecords={employees.totalCount} currentPage={query.lazyParams.page} />
      </Box>
      <AlertCard
        title="Export Confirmation"
        isOpen={!!alertConfig}
        onClose={() => setAlertConfig(null)}
        onConfirm={() => {
          if (alertConfig?.onConfirm) alertConfig.onConfirm();
          setAlertConfig(null);
        }}
        description={alertConfig?.description || ""}
        colorScheme={alertConfig?.colorScheme || "green"}
      />
    </MyDiv >

  );
};
export default EmployeeList;